import { getProgressTextColors } from "../textColors";
import React, { useEffect, useState } from "react";
import { useTheme } from "../../../contexts/ThemeContext";
import { readableColor } from "polished";
import SwipeLeftIcon from '@mui/icons-material/SwipeLeft';
import SwipeRightIcon from '@mui/icons-material/SwipeRight';
import SwipeIcon from '@mui/icons-material/Swipe';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import "./index.css";

export const ProgressTable = ({ children, headers, sortConfig, onSort }) => {
  const theme = useTheme();
  const textColors = getProgressTextColors(theme.mode);
  const tableTextColor = theme.mode === "dark" ? "#ffffff" : textColors.primary;
  const [contrastColor, setContrastColor] = React.useState("#fff");
  const [showSwipeIndicators, setShowSwipeIndicators] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (theme) {
      setContrastColor(readableColor(theme.mode === "dark" ? theme.colors.backgroundPendienteLight : theme.colors.backgroundPendienteDark));
    }
  }, [theme]);

  useEffect(() => {
    // Check if device is mobile
    const checkMobile = () => {
      const ua = navigator.userAgent;
      const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
      setIsMobile(iOS || isMobileDevice);
    };
    
    checkMobile();
    
    // Auto-hide indicators after 5 seconds
    if (isMobile) {
      const timer = setTimeout(() => {
        setShowSwipeIndicators(false);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  return (
    <div
      className="progress-table relative flex flex-col w-full bg-clip-border p-4 sm:p-6"
      style={{
        "--progress-table-text-primary": tableTextColor,
        backgroundColor: theme.mode === "dark" ? "transparent" : "#FFFFFF",
        border: "1px solid #ecb236",
        borderRadius: "8px",
        fontFamily: "'Montserrat', sans-serif",
      }}
    >
      {/* Swipe indicators - only shown on mobile */}
      {isMobile && showSwipeIndicators && (
        <>
          {/* Horizontal swipe indicator */}
          <div 
            className="absolute top-[2.2rem] left-0 right-0 flex justify-end px-4 z-10 pointer-events-none animate-pulse "
            style={{ transform: 'translateY(-50%)' }}
          >
            <SwipeIcon 
              sx={{ 
                fontSize: '3.8rem', 
                color:  '#fff',
                fill:  theme.mode === "dark" ? "#000" : "#fff",
                /* backgroundColor: 'rgba(255,255,255,0.7)', */
                borderRadius: '50%',
                padding: '4px',
                opacity: setTimeout(() => {
                  return 0;
                }, 5000),
               
              }} 
            />
          </div>
        </>
      )}

      {/* Table */}
      <div
        className="w-full overflow-x-auto pb-1"
        style={{
          backgroundColor: theme.mode === "dark" ? "transparent" : "#FFFFFF",
        }}
      >
        <table
          className="w-full text-left table-auto min-w-[700px] laptop:min-w-0"
          style={{
            borderCollapse: "separate",
            borderSpacing: "0 8px",
          }}
        >
          <thead>
            <tr>
              {headers &&
                headers.map((h, idx) => {
                  const isObject = typeof h === "object" && h !== null;
                  const label = isObject ? h.label : h;
                  const key = isObject ? h.key : null;
                  const width = isObject ? h.width : undefined;
                  const headerIcon = isObject ? h.icon : null;
                  const sortable = isObject && h.sortable !== false && typeof onSort === "function";
                  const isSorted = Boolean(sortConfig && sortConfig.key === key);
                  const direction = isSorted ? sortConfig.direction : null;

                  return (
                    <th
                      className={`px-4 py-2 border-none ${sortable ? "cursor-pointer select-none group transition-colors" : ""}`}
                      key={key || idx}
                      scope="col"
                      style={width ? { width, minWidth: width } : undefined}
                      onClick={sortable ? () => onSort(key) : undefined}
                      aria-sort={isSorted ? (direction === "asc" ? "ascending" : "descending") : "none"}
                      role={sortable ? "button" : undefined}
                      tabIndex={sortable ? 0 : undefined}
                      onKeyDown={
                        sortable
                          ? (e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                onSort(key);
                              }
                            }
                          : undefined
                      }
                    >
                      <div className="flex items-center gap-2">
                        {headerIcon && (
                          <span
                            className="inline-flex items-center justify-center w-7 h-7 rounded-lg shrink-0"
                            style={{
                              backgroundColor: "rgba(236, 178, 54, 0.15)",
                              color: "#ecb236",
                              border: "1px solid rgba(236, 178, 54, 0.35)",
                            }}
                          >
                            {headerIcon}
                          </span>
                        )}
                        <p
                          className={`progress-table-heading block leading-none uppercase transition-colors whitespace-nowrap font-montserrat ${
                            headerIcon
                              ? "text-[max(1rem,16px)] font-bold tracking-wider"
                              : "text-[max(1rem,16px)] font-bold tracking-[0.04em]"
                          } ${isSorted ? "font-bold" : ""}`}
                          style={{
                            color: tableTextColor,
                          }}
                        >
                          {label}
                        </p>
                        {sortable && (
                          <span
                            className={`inline-flex items-center transition-all duration-150 ${
                              isSorted
                                ? "opacity-100 scale-100"
                                : "opacity-0 group-hover:opacity-40 scale-90"
                            }`}
                          >
                            {isSorted && direction === "desc" ? (
                              <ArrowDownwardIcon
                                sx={{
                                  fontSize: 14,
                                  color: textColors.primary,
                                }}
                              />
                            ) : (
                              <ArrowUpwardIcon
                                sx={{
                                  fontSize: 14,
                                  color: isSorted
                                    ? (textColors.primary)
                                    : textColors.secondary,
                                }}
                              />
                            )}
                          </span>
                        )}
                      </div>
                    </th>
                  );
                })}
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </div>
  );
};
